/**
 * SetupJourney — the actionable Connect → Verify → Size → Choose scope →
 * Watch stepper, extracted from MonitoringTab so the same machinery renders
 * in two homes:
 *
 *   - Baseline Compliance → Monitoring tab (the standing health surface), and
 *   - the first-run `/setup` wizard (pages/SetupWizard.tsx), which adds the
 *     relay affordance for the "I'm not the Microsoft 365 admin" case.
 *
 * Contract carried over from MonitoringTab verbatim: ALL decision logic and
 * copy live in lib/monitoring-coverage.ts and lib/monitoring-actions.ts (the
 * vitest seams — this harness can't run effects). This component is layout +
 * mutation wiring only. No internal pipeline names (TCM/DSC/…) may appear in
 * anything it renders, and write errors ALWAYS route through
 * mapWriteErrorFrom — never raw ProblemDetails.
 *
 * Local journey state (script text, paste box, sizing job, scope selection)
 * would survive a tenant switch, so hosts render <SetupJourney key={tenantId}>
 * — the key remount resets it without effects.
 *
 * Carbon: the steps are a vertical ProgressIndicator; the current step's
 * action sits inline under that step, with Carbon buttons, fields and inline
 * notifications (components/carbon).
 */

import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import toast from 'react-hot-toast'

import { tcmApi } from '@/lib/api'
import type { CoverageJourney, JourneyStepState } from '@/lib/monitoring-coverage'
import {
  composeCapacityMeter,
  composeJourneyActions,
  composeLicenseGate,
  composeScopeChips,
  composeScopeDefaultCard,
  composeSelectionSummary,
  composeSettingUpRung,
  composeSizingProgress,
  connectScriptFilename,
  EXCHANGE_FOLLOWUP_COPY,
  JOURNEY_TOASTS,
  mapVerifyResult,
  mapWriteErrorFrom,
  parseVerificationOutput,
  SCOPE_PROFILES,
  shouldAutoRetryVerify,
  VERIFY_AUTO_RETRY,
  VERIFY_RETRYING_COPY,
  type LicenseGate,
  type SettingUpRung,
  type TcmScopeResult,
  type TcmSizingStartResult,
  type TcmSizingStatus,
  type VerifyOutcome,
} from '@/lib/monitoring-actions'
import {
  composeCapacityVerdict,
  composeWatchCeiling,
  type CapacityVerdict,
} from '@/lib/monitoring-coverage-capacity'
import { composeRelayEmail } from '@/lib/onboarding-setup'
import type {
  TcmCapacityScan,
  TcmCoverageSummary,
  TcmEnablementVerifyPayload,
  TcmEnablementVerifyResult,
  TcmLicenseEligibility,
} from '@/types/tcm'
import ScopeCoverageModal from '@/components/BaselineCompliance/ScopeCoverageModal'
import { Button } from '@/components/carbon/Button'
import { IconArrowRight } from '@/components/carbon/icons'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { ProgressIndicator, type StepState } from '@/components/carbon/ProgressIndicator'
import { Spinner } from '@/components/carbon/Spinner'
import { TextArea } from '@/components/carbon/TextArea'
import { Tile } from '@/components/carbon/Tile'

export interface SetupJourneyProps {
  tenantId: string
  coverage: TcmCoverageSummary
  /** The composed journey view model (composeMonitoringCoverage().journey). */
  journey: CoverageJourney
  /** Coverage poll exhausted its failure budget — surface "Check again". */
  pollStalled: boolean
  onCheckAgain: () => void
  /**
   * Wizard-only: the "not the Microsoft 365 admin?" relay affordance on the
   * connect/verify steps (Lighthouse copy-email pattern).
   */
  relay?: boolean
}

export default function SetupJourney({
  tenantId,
  coverage,
  journey,
  pollStalled,
  onCheckAgain,
  relay = false,
}: SetupJourneyProps) {
  const queryClient = useQueryClient()

  const actions = composeJourneyActions(coverage)
  // Self-serve auto-finish: a non-interactive, poll-driven rung (no buttons)
  // shown on the Watch step while the server finishes setup, or the GAP-6
  // held state. Non-null only for a marked self-serve tenant in {Finishing,
  // Held}; composeJourneyActions returns no interactive action in those states.
  const settingUpRung = composeSettingUpRung(coverage, pollStalled)

  // ExoProvisioning run 2 must stay reachable even with no in-session verify —
  // e.g. after a page reload the local verifyOutcome is gone, but mail coverage
  // is still required. Seed the follow-up verdict (which carries the "Finish mail
  // coverage" download) from the coverage flag so the download affordance renders
  // regardless. An actual in-session verify still takes precedence below.
  const exoFollowUpOutcome: VerifyOutcome | null = coverage.onboarding.exoProvisioning
    ? mapVerifyResult({ isValid: false, status: 'ExoProvisioning', message: '', probeJobId: null })
    : null

  // License pre-flight (tcm-license-eligibility): queried only while the
  // connect step is current, so the wizard can warn BEFORE the customer runs
  // the 10-minute script. Licensing changes out-of-band → static per session.
  // A failed query leaves data undefined → composeLicenseGate returns null →
  // fail-open (the verify verdict remains the backstop).
  const licenseEligibilityQuery = useQuery<TcmLicenseEligibility>({
    queryKey: ['tcm-license-eligibility', tenantId],
    queryFn: () => tcmApi.getLicenseEligibility(tenantId),
    enabled: !!tenantId && actions?.current?.id === 'connect',
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
  const licenseGate = composeLicenseGate(licenseEligibilityQuery.data)
  // Soft-block: "Proceed anyway" reveals the normal download flow (a license
  // purchase may already be in flight). Local state — the key={tenantId}
  // remount resets it on a tenant switch, like the rest of the journey state.
  const [licenseGateDismissed, setLicenseGateDismissed] = useState(false)

  // Capacity scan (large-client wave 3, H-8): the room the customer's OWN
  // configuration monitors leave for Guardian's watch, licence-aware. Queried
  // only while the scope step is current. It sizes the meter (so an over-room
  // selection holds in the picker instead of failing when the watch is created)
  // and composes the verdict panel. A failed query leaves data undefined and
  // the step renders exactly as before (fail open).
  const capacityQuery = useQuery<TcmCapacityScan>({
    queryKey: ['tcm-capacity', tenantId],
    queryFn: () => tcmApi.getCapacityScan(tenantId),
    enabled: !!tenantId && actions?.current?.id === 'scope',
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  })
  // "Check again" after the customer frees room in Microsoft's portal: bypass
  // the server's five-minute cache and replace the cached scan.
  const recheckCapacityMutation = useMutation<TcmCapacityScan, Error, void>({
    mutationFn: () => tcmApi.getCapacityScan(tenantId, true),
    onSuccess: fresh => {
      queryClient.setQueryData(['tcm-capacity', tenantId], fresh)
      void queryClient.invalidateQueries({ queryKey: ['tcm-coverage', tenantId] })
    },
    onError: err => toast.error(mapWriteErrorFrom(err)),
  })

  const [generatedScript, setGeneratedScript] = useState<string | null>(null)
  const [verifyText, setVerifyText] = useState('')
  const [parseError, setParseError] = useState<string | null>(null)
  const [verifyOutcome, setVerifyOutcome] = useState<VerifyOutcome | null>(null)
  // Silent verify auto-retry (Item 4): on the propagation-lag transient the
  // wizard re-checks with the SAME pasted output on a timer, so a benign delay
  // right after the script runs resolves itself instead of asking the customer
  // to click Verify again. Cadence + retriable decision live in the pure lib
  // (shouldAutoRetryVerify / VERIFY_AUTO_RETRY); the timer wiring lives here.
  const [autoRetrying, setAutoRetrying] = useState(false)
  const verifyInputRef = useRef<{ payload: TcmEnablementVerifyPayload; token: string } | null>(null)
  const autoAttemptsRef = useRef(0)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clearRetryTimer = () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }
  }
  // Clear any pending re-check on unmount (the key={tenantId} remount also
  // resets the refs/state, but the scheduled timer must be cancelled too).
  useEffect(() => clearRetryTimer, [])
  const [sizingJobId, setSizingJobId] = useState<string | null>(null)
  const [selectedProfiles, setSelectedProfiles] = useState<string[]>(() =>
    SCOPE_PROFILES.filter(p => p.preselected).map(p => p.id),
  )
  // The scope picker lives in a dialog launched from the Choose-scope step.
  const [scopeModalOpen, setScopeModalOpen] = useState(false)

  const invalidateCoverage = () =>
    void queryClient.invalidateQueries({ queryKey: ['tcm-coverage', tenantId] })

  // Connect — generate + download the connection script (copy is secondary).
  const connectMutation = useMutation<string, Error, void>({
    mutationFn: () => tcmApi.generateConnectScript(tenantId),
    onSuccess: scriptText => {
      setGeneratedScript(scriptText)
      downloadConnectScript(scriptText, tenantId)
      toast.success(JOURNEY_TOASTS.scriptDownloaded)
      invalidateCoverage()
    },
    onError: err => toast.error(mapWriteErrorFrom(err)),
  })

  // Exchange finish-setup follow-up: downloads the mail-INCLUDED script so the
  // admin completes the still-required mail coverage while in the ExoProvisioning
  // state (the fresh-tenant "run 2"). Distinct from connectMutation (which now
  // renders the mail-INCLUDED default under sequenced-require) so the primary
  // connect path is untouched.
  const exchangeScriptMutation = useMutation<string, Error, void>({
    mutationFn: () => tcmApi.generateConnectScript(tenantId, true),
    onSuccess: scriptText => {
      downloadConnectScript(scriptText, tenantId)
      toast.success(EXCHANGE_FOLLOWUP_COPY.toast)
    },
    onError: err => toast.error(mapWriteErrorFrom(err)),
  })

  // Verify — paste textarea → parseVerificationOutput → mutation.
  const verifyMutation = useMutation<
    TcmEnablementVerifyResult,
    Error,
    { payload: TcmEnablementVerifyPayload; token: string }
  >({
    mutationFn: ({ payload, token }) =>
      tcmApi.verifyConnection(tenantId, payload, token),
    onSuccess: result => {
      const outcome = mapVerifyResult(result)
      setVerifyOutcome(outcome)
      invalidateCoverage()

      // Propagation-lag transient with attempts left → re-check silently on a
      // timer with the same pasted output. No toast (the calm "verifying" panel
      // carries the state); only terminal outcomes toast.
      const willAutoRetry =
        shouldAutoRetryVerify(outcome, autoAttemptsRef.current) && !!verifyInputRef.current
      if (willAutoRetry) {
        autoAttemptsRef.current += 1
        setAutoRetrying(true)
        clearRetryTimer()
        retryTimerRef.current = setTimeout(() => {
          if (verifyInputRef.current) verifyMutation.mutate(verifyInputRef.current)
        }, VERIFY_AUTO_RETRY.intervalMs)
        return
      }

      // Terminal (or budget spent): stop retrying and speak the verdict.
      autoAttemptsRef.current = 0
      setAutoRetrying(false)
      clearRetryTimer()
      if (outcome.tone === 'error') {
        toast.error(outcome.headline)
      } else {
        toast.success(outcome.headline)
      }
    },
    onError: err => {
      // A hard transport error is not the retriable propagation-lag case; stop
      // the loop and surface the mapped write error.
      autoAttemptsRef.current = 0
      setAutoRetrying(false)
      clearRetryTimer()
      toast.error(mapWriteErrorFrom(err))
    },
  })

  const handleVerify = () => {
    const parsed = parseVerificationOutput(verifyText)
    if (!parsed.ok) {
      setParseError(parsed.reason)
      return
    }
    // A manual Verify click starts a fresh attempt budget and cancels any
    // pending silent re-check, so the customer's action always takes over.
    clearRetryTimer()
    autoAttemptsRef.current = 0
    setAutoRetrying(false)
    setParseError(null)
    setVerifyOutcome(null)
    verifyInputRef.current = { payload: parsed.payload, token: parsed.token }
    verifyMutation.mutate({ payload: parsed.payload, token: parsed.token })
  }

  // Size — kick the capture, then poll its status until terminal.
  const sizeMutation = useMutation<TcmSizingStartResult, Error, void>({
    mutationFn: () => tcmApi.startSizing(tenantId),
    onSuccess: job => {
      setSizingJobId(job.jobId)
      // Re-arm a poll that stopped after consecutive failures — the server's
      // idempotent guard returns the SAME jobId on a re-click, so the queryKey
      // (and its error state) would otherwise stay frozen.
      void queryClient.invalidateQueries({ queryKey: ['tcm-sizing-status', tenantId] })
      toast.success(JOURNEY_TOASTS.sizingStarted)
    },
    onError: err => toast.error(mapWriteErrorFrom(err)),
  })

  const sizingStatusQuery = useQuery<TcmSizingStatus>({
    queryKey: ['tcm-sizing-status', tenantId, sizingJobId],
    queryFn: async () => {
      const status = await tcmApi.getSizingStatus(tenantId, sizingJobId!)
      if (status.status !== 'sizing') {
        // Terminal — the journey may have advanced (sized lights the scope
        // step). Same in-queryFn invalidation pattern as the lab's poller.
        void queryClient.invalidateQueries({ queryKey: ['tcm-coverage', tenantId] })
      }
      return status
    },
    enabled: !!tenantId && !!sizingJobId,
    refetchInterval: query => {
      const last = query.state.data as TcmSizingStatus | undefined
      // Stop polling once the capture lands in a terminal state.
      if (last && last.status !== 'sizing') return false
      // Persistent poll failure: stop after 3 consecutive errors instead of
      // spinning forever — the size step renders the honest failed copy and
      // re-enables the button (re-click re-arms via the mutation's invalidate).
      if (query.state.fetchFailureCount >= 3) return false
      return 5_000
    },
    refetchOnWindowFocus: false,
  })

  // Scope — register the chosen coverage profiles, then chain straight into the
  // switch-on so a single customer click sets up coverage AND goes live (one
  // final toast from activateMutation). If activate fails after a good scope,
  // the coverage recomputes to Sized-with-watch-list and composeJourneyActions
  // returns the standalone 'activate' action again as the recovery path — so we
  // don't toast a "scope chosen" half-success here.
  const scopeMutation = useMutation<TcmScopeResult, Error, string[]>({
    mutationFn: profiles => tcmApi.chooseScope(tenantId, profiles),
    onSuccess: () => {
      // Keep the dialog open (showing "Setting up your coverage…") through the
      // chained activate, then let it unmount on its own when coverage recomputes
      // past the scope step — continuous feedback across both legs, and no
      // re-clickable trigger in the brief activate-only window. On a scope error
      // the dialog stays open with the submit re-enabled (onError); on an activate
      // error the journey advances to the watch step's activate recovery action,
      // which unmounts the dialog and surfaces the retry.
      invalidateCoverage()
      activateMutation.mutate()
    },
    onError: err => {
      toast.error(mapWriteErrorFrom(err))
      // The default path submits straight from the scope step with no picker
      // open. Only a 409 (a budget or staged-accept conflict the client-side
      // meter didn't predict) is fixable IN the picker — open it so the
      // customer lands on the live meter and can adjust. Transient/auth/server
      // failures are not picker-fixable: opening it would bury the error toast
      // under a dialog with nothing to trim, so those keep the plain retry.
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setScopeModalOpen(true)
      }
    },
  })

  // Activate — switch continuous watch on.
  const activateMutation = useMutation<void, Error, void>({
    mutationFn: () => tcmApi.activateMonitoring(tenantId),
    onSuccess: () => {
      toast.success(JOURNEY_TOASTS.activated)
      invalidateCoverage()
    },
    onError: err => toast.error(mapWriteErrorFrom(err)),
  })

  const copyScript = (script: string) => {
    void navigator.clipboard.writeText(script).then(
      () => toast.success(JOURNEY_TOASTS.scriptCopied),
      () => toast.error(JOURNEY_TOASTS.scriptCopyFailed),
    )
  }

  const toggleProfile = (id: string, kind: string) => {
    if (kind === 'base') return // always included
    setSelectedProfiles(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id],
    )
  }

  /** The current step's action UI — rendered inline under that step. */
  const renderJourneyAction = (): React.ReactNode => {
    if (!actions?.current) return null
    const action = actions.current

    switch (action.id) {
      case 'connect':
        // License soft-block: a definite "ineligible" pre-flight replaces the
        // download affordance until the customer explicitly proceeds. Eligible /
        // unknown / still-loading all compose null and render the normal flow.
        if (licenseGate && !licenseGateDismissed) {
          return (
            <div className="flex flex-col gap-4">
              <LicenseGatePanel
                gate={licenseGate}
                onProceed={() => setLicenseGateDismissed(true)}
              />
              {relay && <RelayAside tenantId={tenantId} />}
            </div>
          )
        }
        return (
          <div className="flex flex-col gap-4">
            <ActionHelper text={action.helper} />
            <div className="flex flex-wrap items-center gap-4">
              <Button
                kind="primary"
                size="md"
                onClick={() => connectMutation.mutate()}
                disabled={!action.enabled}
                loading={connectMutation.isPending}
              >
                {connectMutation.isPending ? 'Preparing the script…' : action.label}
              </Button>
              {generatedScript && (
                <Button kind="tertiary" size="md" onClick={() => copyScript(generatedScript)}>
                  Copy the script instead
                </Button>
              )}
            </div>
            {!action.enabled && action.disabledReason && (
              <ActionHelper text={action.disabledReason} />
            )}
            <ScriptDocLinks />
            {relay && <RelayAside tenantId={tenantId} />}
          </div>
        )

      case 'verify':
        return (
          <div className="flex flex-col gap-4">
            <ActionHelper text={action.helper} />
            <TextArea
              label="Script output"
              value={verifyText}
              onChange={e => setVerifyText(e.target.value)}
              rows={5}
              spellCheck={false}
              placeholder="Paste the script output here"
              error={parseError ?? undefined}
              className="font-mono text-[12px]"
              wrapperClassName="max-w-xl"
            />
            <div className="flex flex-wrap items-center gap-4">
              <Button
                kind="primary"
                size="md"
                onClick={handleVerify}
                disabled={verifyText.trim().length === 0}
                loading={verifyMutation.isPending || autoRetrying}
              >
                {verifyMutation.isPending || autoRetrying ? 'Checking…' : action.label}
              </Button>
              {actions.canRequestFreshScript && (
                <Button
                  kind="ghost"
                  size="sm"
                  onClick={() => connectMutation.mutate()}
                  disabled={connectMutation.isPending}
                >
                  {connectMutation.isPending ? 'Preparing a fresh script…' : 'Need a fresh script?'}
                </Button>
              )}
            </div>
            {autoRetrying ? (
              <VerifyRetryingPanel />
            ) : (
              (verifyOutcome ?? exoFollowUpOutcome) && (
                <VerifyOutcomePanel
                  outcome={(verifyOutcome ?? exoFollowUpOutcome)!}
                  onAddExchange={() => exchangeScriptMutation.mutate()}
                  addingExchange={exchangeScriptMutation.isPending}
                />
              )
            )}
            {relay && <RelayAside tenantId={tenantId} />}
          </div>
        )

      case 'size': {
        // A poll whose consecutive failures exhausted the retry budget renders
        // the honest failed copy (and canStart re-enables the button) — never
        // an indefinite "capture in progress" over a dead endpoint (house P2).
        // failureCount (not isError/!data) so a single transient blip absorbed
        // by in-cycle retries never flashes "failed", and cached 'sizing' data
        // from an earlier good poll can't mask a dead endpoint (codex P2 ×2).
        // Resets on any successful poll; matches the refetchInterval stop rule.
        const pollFailed = sizingStatusQuery.failureCount >= 3
        const sizing = sizingJobId
          ? pollFailed
            ? ({ status: 'failed' } as Pick<TcmSizingStatus, 'status'>)
            : (sizingStatusQuery.data ?? ({ status: 'sizing' } as Pick<TcmSizingStatus, 'status'>))
          : null
        const progress = sizing ? composeSizingProgress(sizing.status) : null
        const canStart = !sizingJobId || sizing?.status === 'failed'
        return (
          <div className="flex flex-col gap-4">
            <ActionHelper text={action.helper} />
            {progress && (
              <p className="m-0 max-w-xl text-body text-text-secondary">
                <b className="font-semibold text-text-primary">{progress.headline}</b> {progress.body}
              </p>
            )}
            {canStart && (
              <div>
                <Button
                  kind="primary"
                  size="md"
                  onClick={() => sizeMutation.mutate()}
                  disabled={!action.enabled}
                  loading={sizeMutation.isPending}
                >
                  {sizeMutation.isPending ? 'Starting the capture…' : action.label}
                </Button>
              </div>
            )}
          </div>
        )
      }

      case 'scope': {
        // Real per-group counts come from the sizing capture (blueprint D3):
        // the coverage DTO's archived counts first, then the in-session sizing
        // poll, else null (chips stay honestly uncounted, meter reads "sizing").
        const sizedGroups =
          coverage.sizedGroups ?? sizingStatusQuery.data?.sizedGroups ?? null
        const chips = composeScopeChips(sizedGroups, coverage.purviewOptIn)
        // The room the capacity scan left for Guardian's watch (the customer's own
        // monitors share Microsoft's allowance); the coverage figure when unknown.
        const instanceCeiling = composeWatchCeiling(capacityQuery.data, coverage.instanceCeiling)
        // Live capacity verdict — once sized, it gates the submit inside the
        // dialog: an 'over' selection blocks until a workload comes off.
        const meter = composeCapacityMeter(
          sizedGroups,
          selectedProfiles,
          instanceCeiling,
          coverage.purviewOptIn,
        )
        // The advisor's read of that room (null when nothing else shares it).
        // A blocking verdict holds the whole step: nothing can be watched until
        // room is freed, so no submit is offered.
        const capacityVerdict = composeCapacityVerdict(capacityQuery.data, meter.selectedCount)
        const blockedByRoom = capacityVerdict?.blocksSubmit === true
        const selectionSummary = composeSelectionSummary(selectedProfiles, coverage.purviewOptIn)
        const setupPending = scopeMutation.isPending || activateMutation.isPending
        // Security Essentials starts preselected (SCOPE_PROFILES), so by
        // default `selectedProfiles` — and therefore this meter — already
        // reflects it. Only a customer-driven widening inside the picker can
        // push it 'over'; the over state keeps the step on the picker-only
        // fallback (where the meter recommends the trim) instead of a direct
        // submit that would just 409.
        const overBudget = meter.state === 'over'
        const defaultCard = composeScopeDefaultCard(selectionSummary, meter.selectedCount)
        return (
          <div className="flex flex-col gap-4">
            <ActionHelper text={action.helper} />
            {capacityVerdict && (
              <CapacityVerdictPanel
                verdict={capacityVerdict}
                onRecheck={() => recheckCapacityMutation.mutate()}
                rechecking={recheckCapacityMutation.isPending}
              />
            )}
            {blockedByRoom ? null : overBudget ? (
              <>
                {selectionSummary && (
                  <p className="m-0 max-w-xl text-body text-text-secondary">
                    Recommended to start: <b className="font-semibold text-text-primary">{selectionSummary}</b>. Open
                    the picker to choose your coverage, see how closely we can watch, and switch on.
                  </p>
                )}
                <div>
                  <Button kind="primary" size="md" icon={<IconArrowRight />} onClick={() => setScopeModalOpen(true)}>
                    Choose what we watch
                  </Button>
                </div>
              </>
            ) : (
              <Tile className="max-w-xl">
                <p className="m-0 font-semibold text-text-primary">{defaultCard.headline}</p>
                <p className="m-0 mt-1 text-text-secondary">{defaultCard.body}</p>
                <div className="mt-4 flex flex-wrap items-center gap-4">
                  <Button
                    kind="primary"
                    size="md"
                    icon={<IconArrowRight />}
                    onClick={() => scopeMutation.mutate(selectedProfiles)}
                    loading={setupPending}
                  >
                    {setupPending ? 'Setting up your coverage…' : defaultCard.confirmLabel}
                  </Button>
                  <Button kind="ghost" size="sm" onClick={() => setScopeModalOpen(true)} disabled={setupPending}>
                    {defaultCard.adjustLabel}
                  </Button>
                </div>
              </Tile>
            )}
            {scopeModalOpen && !blockedByRoom && (
              <ScopeCoverageModal
                chips={chips}
                meter={meter}
                selectedProfiles={selectedProfiles}
                selectionSummary={selectionSummary}
                instanceCeiling={instanceCeiling}
                submitting={setupPending}
                onToggle={toggleProfile}
                onConfirm={() => scopeMutation.mutate(selectedProfiles)}
                onClose={() => {
                  if (!setupPending) setScopeModalOpen(false)
                }}
              />
            )}
          </div>
        )
      }

      case 'activate':
        return (
          <div className="flex flex-col gap-4">
            <ActionHelper text={action.helper} />
            <div>
              <Button
                kind="primary"
                size="md"
                onClick={() => activateMutation.mutate()}
                disabled={!action.enabled}
                loading={activateMutation.isPending}
              >
                {activateMutation.isPending ? 'Switching on…' : action.label}
              </Button>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <JourneySteps
      journey={journey}
      actionStepId={settingUpRung ? 'watch' : actions?.current?.stepId ?? null}
      actionNode={
        settingUpRung ? (
          <SettingUpRungPanel rung={settingUpRung} onCheckAgain={onCheckAgain} />
        ) : (
          renderJourneyAction()
        )
      }
    />
  )
}

/* -------------------------------------------------------------------------- */
/* Stepper                                                                    */
/* -------------------------------------------------------------------------- */

const STEP_STATE: Record<JourneyStepState, StepState> = {
  done: 'complete',
  current: 'current',
  todo: 'incomplete',
}

/**
 * The journey as Carbon's vertical progress indicator (each step's title and
 * one-line body), with the current step's action inline under that step
 * (`ProgressStep.content`).
 */
function JourneySteps({
  journey,
  actionStepId,
  actionNode,
}: {
  journey: CoverageJourney
  /** Journey step carrying the action UI; null renders read-only. */
  actionStepId?: string | null
  actionNode?: React.ReactNode
}) {
  return (
    <ProgressIndicator
      vertical
      aria-label="Setup steps"
      className="mt-6 max-w-3xl"
      steps={journey.steps.map(step => ({
        id: step.id,
        label: step.title,
        helper: step.body,
        state: STEP_STATE[step.state],
        content: step.id === actionStepId && actionNode ? actionNode : undefined,
      }))}
    />
  )
}

/* -------------------------------------------------------------------------- */
/* Journey action pieces                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Client-side script download per the established blob pattern
 * (BaselineExportTab) — the proxy forwards Content-Disposition, but axios
 * never applies it, so the filename is derived here.
 */
function downloadConnectScript(scriptText: string, tenantId: string) {
  const blob = new Blob([scriptText], { type: 'text/plain' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = connectScriptFilename(tenantId)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

function ActionHelper({ text }: { text: string }) {
  return <p className="m-0 max-w-xl text-body text-text-secondary">{text}</p>
}

/**
 * Two supporting doc links under the connect step (blueprint batch items 3 + 5):
 * what the script does and the access it grants, and a step-by-step for running
 * it. They open in a new tab — the guides live on the public /docs layout — so
 * the customer never loses their place in the wizard.
 */
function ScriptDocLinks() {
  const linkClass = 'text-link underline-offset-2 transition-colors duration-110 ease-carbon hover:text-link-hover hover:underline'
  return (
    <p className="m-0 flex max-w-xl flex-wrap gap-x-4 gap-y-1 text-helper">
      <a href="/docs/permissions" target="_blank" rel="noopener noreferrer" className={linkClass}>
        What this script does and the access it grants
      </a>
      <a href="/docs/onboarding" target="_blank" rel="noopener noreferrer" className={linkClass}>
        New to running a script? Step-by-step here
      </a>
    </p>
  )
}

/**
 * The calm "Guardian is re-checking on its own" notice shown during the silent
 * verify auto-retry (Item 4) — the propagation-lag transient is nobody's fault,
 * so the customer sees "we're on it," not an error. Copy is composed in the
 * pure lib (VERIFY_RETRYING_COPY).
 */
function VerifyRetryingPanel() {
  return (
    <InlineNotification kind="info" title={VERIFY_RETRYING_COPY.headline} className="max-w-xl">
      {VERIFY_RETRYING_COPY.body}
    </InlineNotification>
  )
}

/**
 * The "I'm not the Microsoft 365 admin" relay (wizard only). Discloses a
 * pre-written email the customer copies and sends to whoever holds Global
 * Administrator — acknowledging the two-persona reality of the script step.
 * Copy is composed by the pure lib (composeRelayEmail).
 */
function RelayAside({ tenantId }: { tenantId: string }) {
  const [open, setOpen] = useState(false)
  const email = composeRelayEmail(connectScriptFilename(tenantId))

  const copyEmail = () => {
    void navigator.clipboard
      .writeText(`Subject: ${email.subject}\n\n${email.body}`)
      .then(
        () => toast.success('Email copied. Paste it to your admin.'),
        () => toast.error('We could not reach your clipboard. Select and copy the text below.'),
      )
  }

  return (
    <div className="max-w-xl border-t border-line-subtle pt-3">
      <Button kind="ghost" size="sm" className="-ml-3" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        Not the Microsoft 365 admin? Send it to them
      </Button>
      {open && (
        <Tile className="mt-3">
          <p className="m-0 text-body text-text-secondary">
            Download the script above, then send it to your Global Administrator with
            this note. When they reply with the script&rsquo;s final output, paste it
            here to finish the connection.
          </p>
          <p className="m-0 mt-3 max-h-48 overflow-y-auto whitespace-pre-wrap bg-layer-2 p-3 text-helper text-text-primary">
            {email.body}
          </p>
          <div className="mt-3">
            <Button kind="tertiary" size="sm" onClick={copyEmail}>
              Copy the email
            </Button>
          </div>
        </Tile>
      )}
    </div>
  )
}

/**
 * The non-interactive "Setting up" / held rung (W9). All copy is composed by
 * composeSettingUpRung (pure lib) — this is layout only. The setting-up phase
 * leads with a spinner so the page reads as "working", not "stuck"; the held
 * phase drops it and shows the optional honest hold detail. No buttons either
 * way — there is nothing for the customer to do, except "Check again" when
 * the live poll has given up.
 */
function SettingUpRungPanel({
  rung,
  onCheckAgain,
}: {
  rung: SettingUpRung
  onCheckAgain: () => void
}) {
  const settingUp = rung.phase === 'setting-up'
  const stale = rung.staleConnection
  return (
    <InlineNotification
      kind="info"
      title={
        <span className="inline-flex items-center gap-2">
          {settingUp && !stale ? <Spinner size={16} /> : null}
          {rung.headline}
        </span>
      }
      actions={
        stale ? (
          <Button kind="tertiary" size="sm" onClick={onCheckAgain}>
            Check again
          </Button>
        ) : undefined
      }
      className="max-w-xl"
    >
      {rung.body}
      {rung.detail ? <span className="mt-2 block text-helper text-text-helper">{rung.detail}</span> : null}
    </InlineNotification>
  )
}

/**
 * The scope step's capacity verdict (large-client wave 3, H-8). All copy is
 * composed by composeCapacityVerdict (pure lib) — this is layout only. The
 * blocking state (no room at all) is the Coral error notification; the rest
 * are info. "Check again" re-reads the scan after the customer frees room or
 * adds licenses in Microsoft's portal.
 */
function CapacityVerdictPanel({
  verdict,
  onRecheck,
  rechecking,
}: {
  verdict: CapacityVerdict
  onRecheck: () => void
  rechecking: boolean
}) {
  return (
    <InlineNotification
      kind={verdict.tone}
      title={verdict.headline}
      actions={
        verdict.offerRecheck ? (
          <Button kind="tertiary" size="sm" onClick={onRecheck} loading={rechecking}>
            {rechecking ? 'Checking…' : 'Check again'}
          </Button>
        ) : undefined
      }
      className="max-w-xl"
    >
      {verdict.body}
    </InlineNotification>
  )
}

/**
 * The connect step's license soft-block (tcm-license-eligibility). All copy is
 * composed by composeLicenseGate (pure lib) — this is layout only. Prominent
 * because it saves the customer a 10-minute script run that cannot finish,
 * but never a hard wall: the "Proceed anyway" escape reveals the normal
 * download flow, so it is an info notice, not a blocking error.
 */
function LicenseGatePanel({
  gate,
  onProceed,
}: {
  gate: LicenseGate
  onProceed: () => void
}) {
  return (
    <InlineNotification kind="info" title={gate.headline} className="max-w-xl">
      {gate.body}
      <span className="mt-3 block text-helper text-text-helper">{gate.proceedHelper}</span>
      <span className="mt-2 block">
        <Button kind="ghost" size="sm" className="-ml-3" onClick={onProceed}>
          {gate.proceedLabel}
        </Button>
      </span>
    </InlineNotification>
  )
}

/**
 * Verification verdict — verdict copy composed entirely by mapVerifyResult
 * (pure lib). A failed verification is a blocking error (Coral); success and
 * the caveat are info. On the ExoProvisioning state (outcome.exchangeFollowUp)
 * it also offers the "finish mail coverage" affordance: a button that
 * downloads the mail-included script for the still-required run 2. It never
 * blocks Size/Scope/Watch — those keep working while mail finishes.
 */
function VerifyOutcomePanel({
  outcome,
  onAddExchange,
  addingExchange,
}: {
  outcome: VerifyOutcome
  onAddExchange: () => void
  addingExchange: boolean
}) {
  return (
    <InlineNotification kind={outcome.tone === 'error' ? 'error' : 'info'} title={outcome.headline} className="max-w-xl">
      {outcome.body}
      {outcome.exchangeFollowUp && (
        <span className="mt-3 block border-t border-line-subtle pt-3">
          <span className="block text-helper text-text-secondary">{EXCHANGE_FOLLOWUP_COPY.helper}</span>
          <span className="mt-2 block">
            <Button kind="tertiary" size="sm" onClick={onAddExchange} loading={addingExchange}>
              {addingExchange ? 'Preparing the mail-coverage script…' : EXCHANGE_FOLLOWUP_COPY.label}
            </Button>
          </span>
        </span>
      )}
    </InlineNotification>
  )
}
